// views/AdminFraudView.jsx
// ─────────────────────────────────────────────────────────────
// Fraud holds review queue + fraud settings.
// Renders inside the admin shell — dark theme, no own header.
// Endpoints:
//   GET    /mtrade/admin/fraud/holds?status=pending
//   PATCH  /mtrade/admin/fraud/holds/:id/release
//   PATCH  /mtrade/admin/fraud/holds/:id/reject
//   GET    /mtrade/admin/config?group=fraud
//   PATCH  /mtrade/admin/config/:key
// (Add these routes — see adminFraudRoutes.js in this build)
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { apiClient } from "../../../api/apiServices";

const RISK_CLS = {
  high:   "bg-red-500/15 text-red-400",
  medium: "bg-amber-400/15 text-amber-400",
  low:    "bg-blue-400/15 text-blue-400",
};
const STATUS_CLS = {
  pending:  "bg-amber-400/15 text-amber-400",
  released: "bg-[#00C896]/15 text-[#00C896]",
  rejected: "bg-red-500/15 text-red-400",
};
const SIGNAL_LABELS = {
  shared_nuban:       "Shared bank account",
  shared_push_token:  "Shared device",
  ip_velocity:        "IP velocity",
};

const fmt = n => `₦${Number(n || 0).toLocaleString()}`;

export default function AdminFraudView() {
  const [tab, setTab]         = useState("holds");
  const [holds, setHolds]     = useState([]);
  const [active, setActive]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [note, setNote]       = useState("");
  const [busy, setBusy]       = useState(false);

  // Config state
  const [cfg, setCfg]     = useState({});
  const [cfgLoad, setCfgLoad] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const loadHolds = () => {
    setLoading(true);
    apiClient.get(`/mtrade/admin/fraud/holds?status=${statusFilter}`)
      .then(r => { if (r.data.status) setHolds(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(loadHolds, [statusFilter]);

  useEffect(() => {
    if (tab !== "settings") return;
    apiClient.get("/mtrade/admin/config?group=fraud")
      .then(r => {
        if (r.data.status) {
          const flat = {};
          Object.values(r.data.data || {}).flat().forEach(i => { if (i?.key) flat[i.key] = i.value; });
          setCfg(flat);
        }
      }).catch(() => {}).finally(() => setCfgLoad(false));
  }, [tab]);

  const saveCfg = async (key, value) => {
    setSavingKey(key);
    try {
      await apiClient.patch(`/mtrade/admin/config/${encodeURIComponent(key)}`, { value });
      setCfg(v => ({ ...v, [key]: value }));
    } catch {} finally { setSavingKey(null); }
  };

  const review = async (id, action) => {
    setBusy(true);
    try {
      await apiClient.patch(`/mtrade/admin/fraud/holds/${id}/${action}`, { note: note.trim() });
      setNote(""); setActive(null); loadHolds();
    } catch {} finally { setBusy(false); }
  };

  const Toggle = ({ k, fallback = true }) => (
    <button onClick={() => saveCfg(k, !(cfg[k] ?? fallback))}
      className={`w-11 h-6 rounded-full relative transition-colors flex-shrink-0 ${(cfg[k] ?? fallback) ? "bg-[#00C896]" : "bg-white/10"}`}>
      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${(cfg[k] ?? fallback) ? "left-6" : "left-1"}`} />
    </button>
  );

  const Num = ({ k, fallback = 0 }) => (
    <div className="flex items-center bg-[#1a1a1a] border border-white/8 rounded-lg px-2 focus-within:border-[#00C896]/40">
      <input type="number" defaultValue={cfg[k] ?? fallback}
        onBlur={e => saveCfg(k, Number(e.target.value))}
        className="w-20 bg-transparent py-1.5 text-sm text-white outline-none" />
      {savingKey === k && <span className="text-[#00C896] text-[10px] ml-1">✓</span>}
    </div>
  );

  return (
    <div className="text-white">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-white">Fraud Detection</h1>
          <p className="text-white/40 text-sm mt-0.5">Review held withdrawals and configure fraud rules</p>
        </div>
        <div className="flex gap-1 bg-white/5 rounded-xl p-1">
          {[["holds", "Review Queue"], ["settings", "Settings"]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${tab === v ? "bg-[#00C896]/15 text-[#00C896]" : "text-white/40 hover:text-white/60"}`}>{l}</button>
          ))}
        </div>
      </div>

      {tab === "holds" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Queue */}
          <div className="space-y-3">
            {/* Status filter */}
            <div className="flex gap-2">
              {["pending", "released", "rejected"].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold capitalize transition-all ${statusFilter === s ? "bg-[#00C896]/15 text-[#00C896]" : "bg-white/5 text-white/40"}`}>{s}</button>
              ))}
            </div>

            {loading ? [1,2,3].map(i => <div key={i} className="h-24 bg-white/5 rounded-2xl animate-pulse" />)
            : holds.length === 0 ? (
              <div className="bg-[#161616] border border-white/5 rounded-2xl p-8 text-center">
                <i className="ri-shield-check-line text-[#00C896] text-3xl mb-2 block" />
                <p className="text-white/40 text-sm">No {statusFilter} holds</p>
              </div>
            ) : holds.map(h => (
              <button key={h._id} onClick={() => { setActive(h); setNote(""); }}
                className={`w-full bg-[#161616] border rounded-2xl p-4 text-left transition-colors ${active?._id === h._id ? "border-[#00C896]/40" : "border-white/5 hover:border-white/10"}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${RISK_CLS[h.riskLevel] || RISK_CLS.medium}`}>{h.riskLevel} risk</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_CLS[h.status]}`}>{h.status}</span>
                  </div>
                  <span className="text-white/30 text-[10px]">{new Date(h.createdAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <p className="text-white font-semibold text-sm">{fmt(h.metadata?.amount)} · {h.action.replace("_", " ")}</p>
                <p className="text-white/40 text-xs mt-0.5">
                  {h.signals?.map(s => SIGNAL_LABELS[s.type] || s.type).join(" · ")} · score {h.riskScore}/100
                </p>
              </button>
            ))}
          </div>

          {/* Detail */}
          <div>
            {!active ? (
              <div className="bg-[#161616] border border-white/5 rounded-2xl p-8 text-center text-white/30 text-sm">Select a hold to review</div>
            ) : (
              <div className="bg-[#161616] border border-white/5 rounded-2xl p-5 space-y-4 sticky top-4">
                <div className="flex items-center justify-between">
                  <p className="text-white font-bold">Hold #{active._id.slice(-6)}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${RISK_CLS[active.riskLevel]}`}>{active.riskLevel} risk · {active.riskScore}/100</span>
                </div>

                {/* What was held */}
                <div className="bg-white/4 rounded-xl p-3 space-y-1.5">
                  <p className="text-white/30 text-[10px] uppercase tracking-wide">Action held</p>
                  <p className="text-white text-sm font-semibold capitalize">{active.action.replace(/_/g, " ")}</p>
                  {active.metadata?.amount && <p className="text-[#00C896] font-bold">{fmt(active.metadata.amount)}</p>}
                  {active.metadata?.accountNumber && (
                    <p className="text-white/40 text-xs">{active.metadata.accountName} · {active.metadata.accountNumber} · {active.metadata.bankName}</p>
                  )}
                </div>

                {/* Signals */}
                <div className="space-y-2">
                  <p className="text-white/30 text-[10px] uppercase tracking-wide">Fraud signals</p>
                  {active.signals?.map((s, i) => (
                    <div key={i} className="bg-white/4 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <i className={`text-sm ${s.type === "shared_nuban" ? "ri-bank-line text-red-400" : s.type === "shared_push_token" ? "ri-smartphone-line text-amber-400" : "ri-global-line text-blue-400"}`} />
                        <p className="text-white text-xs font-semibold">{SIGNAL_LABELS[s.type] || s.type}</p>
                      </div>
                      <p className="text-white/40 text-[11px]">{s.detail}</p>
                      {s.relatedUserIds?.length > 0 && (
                        <p className="text-white/25 text-[10px] mt-1">Related accounts: {s.relatedUserIds.slice(0, 3).join(", ")}{s.relatedUserIds.length > 3 ? ` +${s.relatedUserIds.length - 3} more` : ""}</p>
                      )}
                    </div>
                  ))}
                </div>

                {active.status === "pending" && (
                  <>
                    <input value={note} onChange={e => setNote(e.target.value)} placeholder="Review note (optional)"
                      className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-[#00C896]/40" />
                    <div className="flex gap-2">
                      <button onClick={() => review(active._id, "reject")} disabled={busy}
                        className="flex-1 py-2.5 rounded-xl bg-red-500/15 hover:bg-red-500/25 text-red-400 text-sm font-semibold transition-colors">
                        Reject
                      </button>
                      <button onClick={() => review(active._id, "release")} disabled={busy}
                        className="flex-1 py-2.5 rounded-xl bg-[#00C896]/15 hover:bg-[#00C896]/25 text-[#00C896] text-sm font-bold transition-colors">
                        {busy ? "Processing…" : "Release"}
                      </button>
                    </div>
                    <p className="text-white/25 text-[10px]">Release lets the withdrawal proceed. Reject cancels it and notifies the user. Both actions are logged.</p>
                  </>
                )}

                {active.status !== "pending" && (
                  <div className="bg-white/4 rounded-xl p-3">
                    <p className={`text-sm font-bold capitalize ${active.status === "released" ? "text-[#00C896]" : "text-red-400"}`}>{active.status}</p>
                    {active.reviewNote && <p className="text-white/40 text-xs mt-1">"{active.reviewNote}"</p>}
                    {active.reviewedAt && <p className="text-white/25 text-[10px] mt-1">{new Date(active.reviewedAt).toLocaleString("en-NG")}</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="max-w-2xl space-y-5">
          {cfgLoad ? <div className="h-48 bg-white/5 rounded-2xl animate-pulse" />
          : (
            <>
              <div className="bg-[#161616] border border-white/5 rounded-2xl divide-y divide-white/5">
                <div className="p-4 flex items-center justify-between">
                  <div><p className="text-white font-semibold text-sm">Fraud gate</p><p className="text-white/40 text-xs">Master switch — holds suspicious withdrawals for review</p></div>
                  <Toggle k="fraud.withdrawal_gate_enabled" />
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div><p className="text-white font-semibold text-sm">NUBAN dedup</p><p className="text-white/40 text-xs">Flag accounts sharing a bank account number</p></div>
                  <Toggle k="fraud.nuban_dedup_enabled" />
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div><p className="text-white font-semibold text-sm">Device dedup</p><p className="text-white/40 text-xs">Flag accounts sharing a mobile push token</p></div>
                  <Toggle k="fraud.push_token_dedup_enabled" />
                </div>
              </div>

              <div className="bg-[#161616] border border-white/5 rounded-2xl divide-y divide-white/5">
                {[
                  ["fraud.ip_velocity_window_hours", "IP velocity window (hours)", "Accounts from the same IP within this window trigger a signal"],
                  ["fraud.ip_velocity_max_accounts", "IP velocity threshold (accounts)", "How many accounts from one IP before flagging"],
                  ["fraud.min_risk_score_to_hold",   "Minimum score to hold (0-100)", "NUBAN=60, device=50, IP=30 — combined, above this → hold"],
                ].map(([k, label, sub]) => (
                  <div key={k} className="p-4 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm">{label}</p>
                      <p className="text-white/40 text-xs">{sub}</p>
                    </div>
                    <Num k={k} fallback={k.includes("hours") ? 24 : k.includes("max") ? 5 : 40} />
                  </div>
                ))}
              </div>

              <p className="text-white/25 text-xs leading-relaxed">
                <strong className="text-white/40">Risk scores:</strong> NUBAN match = 60 · Device match = 50 · IP velocity = 30. Scores combine; default hold threshold is 40. A NUBAN match alone will hold. IP velocity alone won't unless you lower the threshold below 30.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
